import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export type ReleaseMode = "stable" | "preview";

export const RELEASE_MODE_PARAM = "mode";
export const DEFAULT_RELEASE_MODE: ReleaseMode = "stable";
export const PREVIEW_RELEASE_MODE: ReleaseMode = "preview";

function parseReleaseMode(value: string | null): ReleaseMode | null {
  return value === "stable" || value === "preview" ? value : null;
}

export function useReleaseModeUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const releaseModeRef = useRef(
    parseReleaseMode(
      new URLSearchParams(location.search).get(RELEASE_MODE_PARAM)
    )
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get(RELEASE_MODE_PARAM);
    const releaseMode = parseReleaseMode(modeParam);

    if (modeParam !== null) {
      releaseModeRef.current = releaseMode;
      return;
    }

    if (!releaseModeRef.current) {
      return;
    }

    params.set(RELEASE_MODE_PARAM, releaseModeRef.current);
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);
}

export function useReleaseMode(): ReleaseMode {
  const [params] = useSearchParams();
  return (
    parseReleaseMode(params.get(RELEASE_MODE_PARAM)) ?? DEFAULT_RELEASE_MODE
  );
}
